import React, { useState } from 'react';
import ClientList from './ClientList';
import NewClientForm from './NewClientForm';
import BulkClientUpload from './BulkClientUpload';
import SearchBar from './SearchBar';
import FilterBar from './FilterBar';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ClientSection = ({ 
  clients,
  onSelectClient, 
  onNewMeeting, 
  onSaveNewClient, 
  onBulkUploadClients, 
  searchTerm, 
  onSearch, 
  filters, 
  onFilter,
  canCreateClients,
  canCreateMeetings,
  onUpdateClient,
  onDeleteClient,
  currentUser
}) => {
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [showBulkUploadForm, setShowBulkUploadForm] = useState(false);

  // Filtrar y ordenar clientes dentro de ClientSection
  const filteredAndSortedClients = React.useMemo(() => {
    let currentFiltered = [...clients];

    // Filtrar por usuario si no es administrador
    if (currentUser.role === 'standard') {
      currentFiltered = currentFiltered.filter(client =>
        client.salesExecutive === currentUser.email
      );
    }

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      currentFiltered = currentFiltered.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.specialty && client.specialty.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Aplicar filtro de especialidad
    if (filters.specialty) {
      currentFiltered = currentFiltered.filter(client => 
        client.specialty === filters.specialty
      );
    }

    // Aplicar filtro de célula
    if (filters.cell) {
      currentFiltered = currentFiltered.filter(client => 
        client.cell && client.cell.toLowerCase().includes(filters.cell.toLowerCase())
      );
    }

    // Aplicar filtro de mes (basado en la fecha de las juntas)
    if (filters.month) {
      currentFiltered = currentFiltered.filter(client => 
        client.meetings.some(meeting => {
          const meetingDate = new Date(meeting.date);
          return (meetingDate.getMonth() + 1).toString() === filters.month;
        })
      );
    }

    // Aplicar filtro de fecha específica (buscar en fechas de juntas Y fecha de creación del cliente)
    if (filters.dateFilter) {
      currentFiltered = currentFiltered.filter(client => {
        // Extraer año, mes y día del filtro seleccionado
        const [filterYear, filterMonth, filterDay] = filters.dateFilter.split('-').map(Number);
        
        // Función para comparar solo la fecha (sin hora) en zona horaria de México
        const isSameDateMexico = (dateString) => {
          const date = new Date(dateString);
          // Convertir a zona horaria de México
          const mexicoDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
          
          return mexicoDate.getFullYear() === filterYear &&
                 (mexicoDate.getMonth() + 1) === filterMonth &&
                 mexicoDate.getDate() === filterDay;
        };
        
        // Verificar fecha de creación del cliente
        if (client.createdAt) {
          if (isSameDateMexico(client.createdAt)) {
            return true;
          }
        }
        
        // Verificar fechas de juntas
        return client.meetings && client.meetings.some(meeting => {
          return isSameDateMexico(meeting.date);
        });
      });
    }

    // Aplicar filtro de día de la semana (buscar en fechas de juntas Y fecha de creación del cliente)
    if (filters.dayOfWeek !== '') {
      currentFiltered = currentFiltered.filter(client => {
        // Función para obtener día de la semana en zona horaria de México
        const getMexicoDayOfWeek = (date) => {
          const mexicoDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
          return mexicoDate.getDay().toString();
        };
        
        // Verificar fecha de creación del cliente
        if (client.createdAt) {
          const clientCreatedDate = new Date(client.createdAt);
          if (getMexicoDayOfWeek(clientCreatedDate) === filters.dayOfWeek) {
            return true;
          }
        }
        
        // Verificar fechas de juntas
        return client.meetings && client.meetings.some(meeting => {
          const meetingDate = new Date(meeting.date);
          return getMexicoDayOfWeek(meetingDate) === filters.dayOfWeek;
        });
      });
    }

    // Aplicar filtro de rango de fechas (buscar en fechas de juntas Y fecha de creación del cliente)
    if (filters.dateRange && filters.dateRange.start && filters.dateRange.end) {
      currentFiltered = currentFiltered.filter(client => {
        const startDate = new Date(filters.dateRange.start);
        const endDate = new Date(filters.dateRange.end);
        
        // Función para convertir fecha a zona horaria de México
        const getMexicoDate = (date) => {
          return new Date(date.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
        };
        
        // Verificar fecha de creación del cliente
        if (client.createdAt) {
          const clientCreatedDate = getMexicoDate(new Date(client.createdAt));
          if (clientCreatedDate >= startDate && clientCreatedDate <= endDate) {
            return true;
          }
        }
        
        // Verificar fechas de juntas
        return client.meetings && client.meetings.some(meeting => {
          const meetingDate = getMexicoDate(new Date(meeting.date));
          return meetingDate >= startDate && meetingDate <= endDate;
        });
      });
    }

    // Aplicar ordenamiento
    currentFiltered.sort((a, b) => {
      let comparison = 0;
      
      switch(filters.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'specialty':
          comparison = (a.specialty || '').localeCompare(b.specialty || '');
          break;
        case 'conversion':
          const aTotalContacts = a.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const aTotalPatients = a.meetings.reduce((total, m) => total + m.patients, 0);
          const aRate = aTotalContacts > 0 ? aTotalPatients / aTotalContacts : 0;
          
          const bTotalContacts = b.meetings.reduce((total, m) => total + m.contactsGenerated, 0);
          const bTotalPatients = b.meetings.reduce((total, m) => total + m.patients, 0);
          const bRate = bTotalContacts > 0 ? bTotalPatients / bTotalContacts : 0;
          
          comparison = aRate - bRate;
          break;
        case 'date':
          const aLastMeeting = a.meetings.length > 0 ? Math.max(...a.meetings.map(m => new Date(m.date).getTime())) : 0;
          const bLastMeeting = b.meetings.length > 0 ? Math.max(...b.meetings.map(m => new Date(m.date).getTime())) : 0;
          comparison = aLastMeeting - bLastMeeting;
          break;
        default:
          comparison = 0;
      }
      
      return filters.sortOrder === 'desc' ? -comparison : comparison;
    });

    return currentFiltered;
  }, [clients, searchTerm, filters]);


  const handleNewClientClick = () => {
    setShowNewClientForm(true);
    setShowBulkUploadForm(false);
  };

  const handleBulkUploadClick = () => {
    setShowBulkUploadForm(true);
    setShowNewClientForm(false);
  };

  const handleCancelForm = () => {
    setShowNewClientForm(false);
    setShowBulkUploadForm(false);
  };

  // Función para formatear fecha corta en zona horaria de México
  const formatShortDateForMexico = (dateString) => {
    if (!dateString) return '';
    
    const dateOnly = dateString.split(' ')[0];
    const [year, month, day] = dateOnly.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('es-MX');
  };

  const generateAllClientsPDF = async () => {
    try {
      alert('Iniciando generación de PDF...');
      
      // Mostrar mensaje de carga
      const loadingMessage = document.createElement('div');
      loadingMessage.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8); z-index: 9999; display: flex; justify-content: center; align-items: center; color: white; font-size: 18px;">
          <div style="text-align: center;">
            <div style="margin-bottom: 20px;">Generando PDF de todos los clientes...</div>
            <div style="width: 50px; height: 50px; border: 3px solid #f3f3f3; border-top: 3px solid #0061A8; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>
          </div>
        </div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      `;
      document.body.appendChild(loadingMessage);

      // Crear un contenedor temporal para el PDF
      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute';
      pdfContainer.style.left = '-9999px';
      pdfContainer.style.width = '210mm';
      pdfContainer.style.backgroundColor = 'white';
      pdfContainer.style.fontFamily = 'Arial, sans-serif';
      pdfContainer.style.padding = '20mm';
      pdfContainer.style.boxSizing = 'border-box';

      // Header
      const header = `
        <div style="border-bottom: 3px solid #0061A8; padding-bottom: 20px; margin-bottom: 30px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <h1 style="color: #0061A8; font-size: 28px; margin: 0; font-weight: bold;">DOCTORWEB</h1>
              <p style="color: #63666A; margin: 5px 0 0 0; font-size: 14px;">Agencia de Marketing Digital para Médicos</p>
            </div>
            <div style="text-align: right; color: #63666A; font-size: 12px;">
              <p style="margin: 0;">Fecha de generación: ${new Date().toLocaleDateString('es-MX', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
              <p style="margin: 5px 0 0 0;">Reporte General de Clientes</p>
            </div>
          </div>
        </div>
      `;

      // Resumen general
      const summary = `
        <div style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); padding: 25px; border-radius: 10px; margin-bottom: 30px; border-left: 5px solid #0061A8;">
          <h2 style="color: #0061A8; font-size: 24px; margin: 0 0 15px 0; font-weight: bold;">📊 RESUMEN GENERAL</h2>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; text-align: center;">
            <div style="background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
              <h3 style="margin: 0 0 5px 0; font-size: 14px; color: #63666A;">Total Clientes</h3>
              <p style="margin: 0; font-size: 24px; font-weight: bold; color: #0061A8;">${totalClients}</p>
            </div>
            <div style="background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
              <h3 style="margin: 0 0 5px 0; font-size: 14px; color: #63666A;">Clientes Activos</h3>
              <p style="margin: 0; font-size: 24px; font-weight: bold; color: #A8D08D;">${activeClients}</p>
            </div>
            <div style="background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
              <h3 style="margin: 0 0 5px 0; font-size: 14px; color: #63666A;">Total Juntas</h3>
              <p style="margin: 0; font-size: 24px; font-weight: bold; color: #63666A;">${totalMeetings}</p>
            </div>
            <div style="background: white; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
              <h3 style="margin: 0 0 5px 0; font-size: 14px; color: #63666A;">Especialidades</h3>
              <p style="margin: 0; font-size: 24px; font-weight: bold; color: #0061A8;">${new Set(filteredAndSortedClients.map(c => c.specialty).filter(Boolean)).size}</p>
            </div>
          </div>
        </div>
      `;

      // Lista de clientes
      let clientsList = `
        <div style="background: white; padding: 25px; border-radius: 10px; margin-bottom: 30px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); border-left: 5px solid #63666A;">
          <h2 style="color: #0061A8; font-size: 20px; margin: 0 0 20px 0; font-weight: bold;">👥 LISTA DE CLIENTES</h2>
      `;

      filteredAndSortedClients.forEach((client, index) => {
        const totalContacts = client.meetings.reduce((sum, m) => sum + m.contactsGenerated, 0);
        const totalPatients = client.meetings.reduce((sum, m) => sum + m.patients, 0);
        const conversionRate = totalContacts > 0 ? ((totalPatients / totalContacts) * 100).toFixed(1) + '%' : '0%';
        const lastMeetingDate = client.meetings.length > 0 ? 
          formatShortDateForMexico(client.meetings[client.meetings.length - 1].date) : 'Sin juntas';

        clientsList += `
          <div style="border: 1px solid #e9ecef; border-radius: 8px; padding: 15px; margin-bottom: 15px; background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); page-break-inside: avoid;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <h3 style="color: #0061A8; margin: 0; font-size: 16px; font-weight: bold;">
                ${index + 1}. ${client.name}
              </h3>
              <div style="background: ${client.meetings.length > 0 ? '#A8D08D' : '#63666A'}; color: white; padding: 3px 10px; border-radius: 15px; font-size: 10px;">
                ${client.meetings.length > 0 ? 'ACTIVO' : 'INACTIVO'}
              </div>
            </div>
            
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; font-size: 12px;">
              <div>
                ${client.specialty ? `<p style="margin: 2px 0;"><strong>Especialidad:</strong> ${client.specialty}</p>` : ''}
                ${client.cell ? `<p style="margin: 2px 0;"><strong>Célula:</strong> ${client.cell}</p>` : ''}
                ${client.email ? `<p style="margin: 2px 0;"><strong>Email:</strong> ${client.email}</p>` : ''}
              </div>
              <div>
                ${client.country ? `<p style="margin: 2px 0;"><strong>País:</strong> ${client.country}</p>` : ''}
                ${client.salesExecutive ? `<p style="margin: 2px 0;"><strong>Ejecutivo:</strong> ${client.salesExecutive}</p>` : ''}
                <p style="margin: 2px 0;"><strong>Última junta:</strong> ${lastMeetingDate}</p>
              </div>
            </div>

            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; text-align: center; font-size: 11px;">
              <div style="background: #e3f2fd; padding: 8px; border-radius: 4px;">
                <p style="margin: 0; color: #63666A;">Juntas</p>
                <p style="margin: 2px 0 0 0; font-weight: bold; color: #0061A8;">${client.meetings.length}</p>
              </div>
              <div style="background: #e8f5e8; padding: 8px; border-radius: 4px;">
                <p style="margin: 0; color: #63666A;">Contactos</p>
                <p style="margin: 2px 0 0 0; font-weight: bold; color: #0061A8;">${totalContacts}</p>
              </div>
              <div style="background: #f3e5f5; padding: 8px; border-radius: 4px;">
                <p style="margin: 0; color: #63666A;">Pacientes</p>
                <p style="margin: 2px 0 0 0; font-weight: bold; color: #0061A8;">${totalPatients}</p>
              </div>
              <div style="background: #fff3e0; padding: 8px; border-radius: 4px;">
                <p style="margin: 0; color: #63666A;">Conversión</p>
                <p style="margin: 2px 0 0 0; font-weight: bold; color: #0061A8;">${conversionRate}</p>
              </div>
            </div>
          </div>
        `;
      });

      clientsList += `</div>`;

      // Footer
      const footer = `
        <div style="border-top: 2px solid #0061A8; padding-top: 20px; margin-top: 40px; text-align: center; color: #63666A; font-size: 12px;">
          <p style="margin: 0 0 5px 0;"><strong>DOCTORWEB - Agencia de Marketing Digital para Médicos</strong></p>
          <p style="margin: 0 0 5px 0;">Reporte generado automáticamente el ${new Date().toLocaleDateString('es-MX')} a las ${new Date().toLocaleTimeString('es-MX')}</p>
          <p style="margin: 0; font-style: italic;">Este documento contiene información confidencial de los clientes.</p>
        </div>
      `;

      // Combinar todo el contenido
      pdfContainer.innerHTML = header + summary + clientsList + footer;
      document.body.appendChild(pdfContainer);

      // Generar el PDF con mejor manejo de páginas
      const canvas = await html2canvas(pdfContainer, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: pdfContainer.scrollWidth,
        height: pdfContainer.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Limpiar elementos temporales de forma segura
      try {
        if (pdfContainer && document.body.contains(pdfContainer)) {
          document.body.removeChild(pdfContainer);
        }
      } catch (e) {
        console.warn('Error al remover pdfContainer:', e);
      }

      try {
        if (loadingMessage && document.body.contains(loadingMessage)) {
          document.body.removeChild(loadingMessage);
        }
      } catch (e) {
        console.warn('Error al remover loadingMessage:', e);
      }

      const fileName = `Reporte_Todos_los_Clientes_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      alert('¡PDF de todos los clientes generado exitosamente!');

    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al generar el PDF. Por favor, inténtalo de nuevo.');
      
      // Limpiar elementos de forma segura en caso de error
      try {
        const pdfContainer = document.querySelector('[style*="position: absolute"][style*="left: -9999px"]');
        if (pdfContainer && document.body.contains(pdfContainer)) {
          document.body.removeChild(pdfContainer);
        }
      } catch (e) {
        console.warn('Error al limpiar pdfContainer en catch:', e);
      }

      try {
        const loadingMessage = document.querySelector('[style*="position: fixed"]');
        if (loadingMessage && document.body.contains(loadingMessage)) {
          document.body.removeChild(loadingMessage);
        }
      } catch (e) {
        console.warn('Error al limpiar loadingMessage en catch:', e);
      }
    }
  };

  // Calcular métricas para las tarjetas de resumen
  const totalClients = filteredAndSortedClients.length;
  const activeClients = filteredAndSortedClients.filter(c => c.meetings.length > 0).length;
  const totalMeetings = filteredAndSortedClients.reduce((sum, client) => sum + client.meetings.length, 0);
  const recentMeetings = filteredAndSortedClients.filter(c => 
    c.meetings.some(m => new Date(m.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  ).length;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#0061A8]">Gestión de Clientes</h2>
          <p className="text-sm text-[#63666A] mt-1">
            {totalClients} clientes en total, {activeClients} activos
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={generateAllClientsPDF}
            className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Generar PDF
          </button>
          {canCreateClients && (
            <>
              <button
                onClick={handleNewClientClick}
                className="bg-[#A8D08D] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Nuevo Cliente
              </button>
              {currentUser.role === 'admin' && (
                <button
                  onClick={handleBulkUploadClick}
                  className="bg-[#A8D08D] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors flex items-center"
                >
                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Carga Masiva
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tarjetas de Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#0061A8]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Total Clientes</h4>
          <p className="text-2xl font-bold text-[#0061A8]">{totalClients}</p>
          <p className="text-xs text-[#63666A] mt-1">
            {activeClients} clientes activos
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#A8D08D]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Total Juntas</h4>
          <p className="text-2xl font-bold text-[#0061A8]">{totalMeetings}</p>
          <p className="text-xs text-[#63666A] mt-1">
            {recentMeetings} clientes con juntas este mes
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#63666A]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Por Especialidad</h4>
          <p className="text-2xl font-bold text-[#0061A8]">
            {new Set(filteredAndSortedClients.map(c => c.specialty).filter(Boolean)).size}
          </p>
          <p className="text-xs text-[#63666A] mt-1">
            especialidades diferentes
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#0061A8]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Por Célula</h4>
          <p className="text-2xl font-bold text-[#0061A8]">
            {new Set(filteredAndSortedClients.map(c => c.cell).filter(Boolean)).size}
          </p>
          <p className="text-xs text-[#63666A] mt-1">
            células diferentes
          </p>
        </div>
      </div>

      {showNewClientForm ? (
        <NewClientForm
          onSave={onSaveNewClient}
          onCancel={handleCancelForm}
        />
      ) : showBulkUploadForm ? (
        <BulkClientUpload
          onUpload={onBulkUploadClients}
          onCancel={handleCancelForm}
        />
      ) : (
        <>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="mb-4">
              <SearchBar onSearch={onSearch} />
            </div>
            <div>
              <FilterBar onFilter={onFilter} />
            </div>
          </div>
          <ClientList 
            clients={filteredAndSortedClients}
            onSelectClient={onSelectClient}
            onNewMeeting={canCreateMeetings ? onNewMeeting : null}
            onUpdateClient={onUpdateClient}
            onDeleteClient={onDeleteClient}
            currentUser={currentUser}
          />
        </>
      )}
    </div>
  );
};

export default ClientSection;
